import React, { useState, useMemo } from 'react';
import ToolLayout from './ToolLayout';

const GENRES = [
  { id: 'drama', label: 'Drama', dailyRate: 12000 },
  { id: 'thriller', label: 'Thriller', dailyRate: 16000 },
  { id: 'horror', label: 'Horror', dailyRate: 9000 },
  { id: 'comedy', label: 'Comedy', dailyRate: 11000 },
  { id: 'documentary', label: 'Documentary', dailyRate: 6000 },
  { id: 'action', label: 'Action', dailyRate: 22000 },
  { id: 'scifi', label: 'Sci-Fi', dailyRate: 20000 },
  { id: 'animation', label: 'Animation', dailyRate: 18000 },
];

const DEPARTMENTS = [
  { name: 'Above the Line', desc: 'Director, writers, producers, cast', pct: 0.28, color: 'bg-sky-500' },
  { name: 'Production Crew & Labor', desc: 'All below-the-line crew', pct: 0.20, color: 'bg-violet-500' },
  { name: 'Camera & Lighting', desc: 'Equipment packages, DP, gaffers', pct: 0.12, color: 'bg-emerald-500' },
  { name: 'Art & Production Design', desc: 'Sets, props, wardrobe, makeup', pct: 0.10, color: 'bg-amber-500' },
  { name: 'Locations & Travel', desc: 'Permits, transport, accommodation', pct: 0.08, color: 'bg-orange-500' },
  { name: 'Post Production', desc: 'Edit, color, VFX, sound, music', pct: 0.14, color: 'bg-rose-500' },
  { name: 'Insurance & Legal', desc: 'E&O, liability, contracts', pct: 0.05, color: 'bg-indigo-500' },
  { name: 'Contingency', desc: 'Overages and unexpected costs', pct: 0.03, color: 'bg-zinc-500' },
];

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function getTier(n) {
  if (n < 300000) return 'MICRO BUDGET';
  if (n < 1000000) return 'LOW BUDGET';
  if (n < 5000000) return 'MID BUDGET';
  return 'STUDIO LEVEL';
}

export default function ProductionBudgetEstimator() {
  const [genre, setGenre] = useState('drama');
  const [shootDays, setShootDays] = useState(20);
  const [castSize, setCastSize] = useState(5);

  const { total, departments } = useMemo(() => {
    const g = GENRES.find((g) => g.id === genre);
    const castAdj = Math.max(0, castSize - 5) * 0.025;
    const total = Math.round(shootDays * g.dailyRate * (1 + castAdj));
    const departments = DEPARTMENTS.map((d) => ({ ...d, amount: Math.round(total * d.pct) }));
    return { total, departments };
  }, [genre, shootDays, castSize]);

  return (
    <ToolLayout
      accent="Aperture Slate // Production Tools"
      title="Budget"
      titleAccent="Estimator"
      description="Enter your genre, shoot length, and cast size to get a department-by-department budget breakdown."
    >
      <div className="border border-zinc-800 bg-zinc-950 p-8 space-y-8">

        {/* Genre */}
        <div>
          <label className="block text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-3">Genre</label>
          <div className="grid grid-cols-4 gap-2">
            {GENRES.map((g) => (
              <button
                key={g.id}
                onClick={() => setGenre(g.id)}
                className={`py-2.5 text-[11px] font-bold tracking-widest border transition-all duration-200 ${
                  genre === g.id
                    ? 'border-sky-500 text-sky-400 bg-sky-500/10'
                    : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Shoot Days */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Shoot Days</label>
            <span className="text-2xl font-black tabular-nums">{shootDays} days</span>
          </div>
          <input
            type="range" min={5} max={90} step={1} value={shootDays}
            onChange={(e) => setShootDays(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>5 days</span><span>90 days</span>
          </div>
        </div>

        {/* Cast Size */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Speaking Cast Size</label>
            <span className="text-2xl font-black tabular-nums">{castSize} actors</span>
          </div>
          <input
            type="range" min={1} max={30} step={1} value={castSize}
            onChange={(e) => setCastSize(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>1</span><span>30</span>
          </div>
        </div>

        <div className="border-t border-zinc-800" />

        {/* Total */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-2">Estimated Budget</p>
            <span className="text-[10px] border border-zinc-700 text-zinc-500 px-2 py-0.5 tracking-widest">
              {getTier(total)}
            </span>
          </div>
          <p className="text-4xl font-black tabular-nums text-white">{fmt(total)}</p>
        </div>

        {/* Department Breakdown */}
        <div>
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-5">Department Breakdown</p>
          <div className="space-y-4">
            {departments.map((d) => (
              <div key={d.name}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] text-zinc-300 tracking-wide font-semibold whitespace-nowrap">{d.name}</span>
                    <span className="text-[10px] text-zinc-600 truncate hidden sm:block">{d.desc}</span>
                  </div>
                  <span className="text-sm font-bold text-white tabular-nums ml-3 shrink-0">{fmt(d.amount)}</span>
                </div>
                <div className="h-2 w-full bg-zinc-900 rounded-sm overflow-hidden">
                  <div
                    className={`h-full rounded-sm transition-all duration-700 ${d.color}`}
                    style={{ width: `${(d.pct / 0.28) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
          Estimates based on industry averages for US independent productions. Actual costs vary by location, union status, and complexity. Daily rates assume non-union crew. Consult a line producer for accurate budgeting.
        </p>
      </div>
    </ToolLayout>
  );
}
